import React, { useId, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { BASE_URL } from '../../constants';
import { Btn, Card, ErrorState, Field, Loading, PageHeader } from '../ForBrands/ui/BrandUI';
import { usePhoneTheme } from '../ForBrands/useIsPhone';
import AuthFrame from './v2/AuthFrame';
import { Actions, EyeIcon, FieldLabel, Note, PrimaryBtn, TitleBlock } from './v2/pieces';
import '../ForBrands/brandMode.css';

// Change password in the V2 style. Every signed-in account changes it inside
// its own mode shell (/coach, /rep, /athlete, /brand, /supporter, /users for
// admins, Cache AI staff and interns, /business for the Player Index roles),
// where the layout supplies the frame. /change-password itself only serves
// the first log in of an invited admin (?firstLogin=1, on the V2 auth
// frame); any other visit, such as an old link, goes on to the viewer's
// in-shell page.

const SHELLS = [
  { prefix: '/coach', eyebrow: 'Coach Mode' },
  { prefix: '/rep', eyebrow: 'Rep Mode' },
  { prefix: '/athlete', eyebrow: 'Athlete Mode' },
  { prefix: '/brand', eyebrow: 'Brand Mode' },
  { prefix: '/supporter', eyebrow: 'Supporter' },
  { prefix: '/users', eyebrow: 'Admin Mode' },
  { prefix: '/business', eyebrow: 'Player Index' },
];

// A shell owns its prefix and everything under it ("/users/password"), not a
// path that only starts with the same letters.
const shellFor = (pathname = '') => SHELLS.find((s) => pathname === s.prefix || pathname.startsWith(`${s.prefix}/`)) || null;

// The in-shell password page for a signed-in account, in the order the log
// in page picks a home: staff first, then coach, brand, the other Player
// Index roles, athlete, intern, rep and supporter. Null when no role is
// known.
export const passwordPathFor = (permissions = []) => {
  const perms = Array.isArray(permissions) ? permissions : [];
  const has = (p) => perms.includes(p);
  if (has('IS_ADMIN') || has('IS_CACHE_AI')) return '/users/password';
  if (has('IS_COACH')) return '/coach/password';
  if (has('IS_BRAND_AGENCY')) return '/brand/password';
  if (has('IS_RECRUITER_SCOUT') || has('IS_AD_PD')) return '/business/password';
  if (has('IS_ATHLETE')) return '/athlete/password';
  if (has('IS_INTERN')) return '/users/password';
  if (has('IS_REP')) return '/rep/password';
  if (has('IS_SUPPORTER')) return '/supporter/password';
  return null;
};

const CHECKS = [
  { key: 'length', label: 'At least 6 characters', test: (p) => p.length >= 6 },
  { key: 'upper', label: 'Contains an uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', label: 'Contains a number', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Contains a special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

// `size` picks the input: "shell" is the 40px BrandUI field, "frame" the
// 56px field of the auth frame.
const PasswordInput = ({ id, value, onChange, autoComplete, placeholder, show, onToggle, size = 'shell' }) => (
  <span className="relative block">
    <input
      id={id}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      autoComplete={autoComplete}
      placeholder={placeholder}
      className={size === 'frame' ? 'ob-input !pr-12' : 'bm-input !pr-11'}
    />
    <button
      type="button"
      onClick={onToggle}
      aria-label={show ? 'Hide password' : 'Show password'}
      className={`absolute top-1/2 -translate-y-1/2 text-bm-tertiary hover:text-bm-ink ${size === 'frame' ? 'right-[18px]' : 'right-3'}`}
    >
      <EyeIcon off={show} />
    </button>
  </span>
);

const CheckDot = ({ ok }) => (
  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${ok ? 'bg-bm-positive' : 'bg-bm-mutedbg'}`}>
    {ok && (
      <svg width="9" height="7" viewBox="0 0 11 9" fill="none" stroke="currentColor" className="text-white">
        <path d="M1 4.5l3 3L10 1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

const CheckList = ({ password }) => (
  <ul className="flex flex-col gap-2">
    {CHECKS.map((c) => {
      const ok = c.test(password);
      return (
        <li key={c.key} className="flex items-center gap-[10px]">
          <CheckDot ok={ok} />
          <span className={`bm-body-m ${ok ? 'text-bm-ink' : 'text-bm-muted'}`}>{c.label}</span>
        </li>
      );
    })}
  </ul>
);

// The form state and the request, shared by the in-shell page and the first
// log in frame.
const usePasswordForm = (isFirstLogin) => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState({ old: false, new: false, confirm: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const toggle = (key) => setShow((s) => ({ ...s, [key]: !s[key] }));
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const incomplete = !oldPassword || !newPassword || !confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Shown passwords are hidden again as the form submits, so password
    // managers read password fields (as the V2 PasswordInput does).
    setShow({ old: false, new: false, confirm: false });
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/users/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(isFirstLogin ? 'Password set. Welcome to CACHE.' : 'Password changed.');
        if (isFirstLogin) {
          // Invited admin: land on the admin user-management home so they
          // can pick up where the invite intended.
          navigate('/users');
        } else {
          navigate(-1);
        }
      } else {
        setError(data?.error || 'Could not change the password.');
      }
    } catch (err) {
      setError('Could not change the password.');
    }
    setLoading(false);
  };

  return {
    navigate, oldPassword, setOldPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    show, toggle, error, loading, mismatch, incomplete, handleSubmit,
  };
};

// Inside a mode shell: the shell draws the topbar and the gutter. In the
// Athlete and Coach phone theme the fields take the 56px, 16px phone form
// (bm-phone-form, DESIGN.md 5.5), so iOS does not zoom in on a tap.
const ShellPasswordPage = ({ eyebrow, isFirstLogin }) => {
  const f = usePasswordForm(isFirstLogin);
  const phone = usePhoneTheme();
  return (
    <div className={phone ? 'bm-phone-form' : undefined}>
      <PageHeader
        eyebrow={eyebrow}
        title={isFirstLogin ? 'Set your password' : 'Change password'}
        subtitle={isFirstLogin ? 'Enter the temporary password you were emailed, then choose your own.' : 'Choose a new password for your CACHE account.'}
        actions={
          !isFirstLogin ? (
            <Btn variant="secondary" onClick={() => f.navigate(-1)}>
              Back
            </Btn>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 pt-2 lg:flex-row lg:items-start">
        <Card padding="p-6" className="w-full max-w-[560px]">
          <form onSubmit={f.handleSubmit} className="flex flex-col gap-5">
            <Field label={isFirstLogin ? 'Temporary password' : 'Current password'}>
              <PasswordInput value={f.oldPassword} onChange={f.setOldPassword} autoComplete="current-password" show={f.show.old} onToggle={() => f.toggle('old')} />
            </Field>
            <Field label="New password">
              <PasswordInput value={f.newPassword} onChange={f.setNewPassword} autoComplete="new-password" show={f.show.new} onToggle={() => f.toggle('new')} />
            </Field>
            <Field label="Confirm new password" hint={f.mismatch ? 'These passwords do not match yet.' : undefined}>
              <PasswordInput value={f.confirmPassword} onChange={f.setConfirmPassword} autoComplete="new-password" show={f.show.confirm} onToggle={() => f.toggle('confirm')} />
            </Field>
            {f.error && <div role="alert"><ErrorState>{f.error}</ErrorState></div>}
            <div className="flex items-center gap-3 border-t border-bm-border pt-5">
              <Btn type="submit" disabled={f.loading || f.incomplete}>
                {f.loading ? 'Saving...' : isFirstLogin ? 'Set password' : 'Save new password'}
              </Btn>
              {!isFirstLogin && (
                <Btn variant="ghost" onClick={() => f.navigate(-1)}>
                  Cancel
                </Btn>
              )}
            </div>
          </form>
        </Card>

        <Card padding="p-6" className="flex w-full flex-col gap-3 lg:w-[360px]">
          <span className="bm-overline">A strong password</span>
          <CheckList password={f.newPassword} />
          <p className="bm-body-s text-bm-tertiary">You stay signed in on this device after the change.</p>
        </Card>
      </div>
    </div>
  );
};

const FrameField = ({ id, label, hint, children }) => (
  <div className="flex flex-col gap-[7px]">
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    {children}
    {hint && <p className="bm-body-s text-bm-tertiary">{hint}</p>}
  </div>
);

// An invited admin's first log in (auth/v2 LoginPage and GoogleButton send
// mustChangePassword here with ?firstLogin=1): the temporary password must
// be replaced before the app opens, so the page stands alone on the V2 auth
// frame with no back button, and success lands on /users.
const FirstLoginPasswordPage = () => {
  const f = usePasswordForm(true);
  const id = useId();
  return (
    <AuthFrame title="Set your password">
      <form className="contents" onSubmit={f.handleSubmit}>
        <TitleBlock eyebrow="Welcome to CACHE" title="Choose your password" sub="Enter the temporary password you were emailed, then choose your own." />
        <FrameField id={`${id}-old`} label="Temporary password">
          <PasswordInput id={`${id}-old`} size="frame" value={f.oldPassword} onChange={f.setOldPassword} autoComplete="current-password" placeholder="Enter the emailed password" show={f.show.old} onToggle={() => f.toggle('old')} />
        </FrameField>
        <FrameField id={`${id}-new`} label="New password">
          <PasswordInput id={`${id}-new`} size="frame" value={f.newPassword} onChange={f.setNewPassword} autoComplete="new-password" placeholder="Enter new password" show={f.show.new} onToggle={() => f.toggle('new')} />
        </FrameField>
        <FrameField id={`${id}-confirm`} label="Confirm new password" hint={f.mismatch ? 'These passwords do not match yet.' : undefined}>
          <PasswordInput id={`${id}-confirm`} size="frame" value={f.confirmPassword} onChange={f.setConfirmPassword} autoComplete="new-password" placeholder="Confirm new password" show={f.show.confirm} onToggle={() => f.toggle('confirm')} />
        </FrameField>
        <div className="flex flex-col gap-3 rounded-[16px] bg-bm-subtle px-[18px] py-4">
          <span className="bm-overline">A strong password</span>
          <CheckList password={f.newPassword} />
        </div>
        {f.error && <div role="alert"><Note tone="critical">{f.error}</Note></div>}
        <Actions>
          <PrimaryBtn type="submit" disabled={f.incomplete} loading={f.loading}>Set password</PrimaryBtn>
        </Actions>
      </form>
    </AuthFrame>
  );
};

// /change-password with no shell around it.
const StandalonePassword = ({ isFirstLogin }) => {
  const permissions = useSelector((state) => state.user?.user?.permissions);
  const loadError = useSelector((state) => state.user?.loadError);
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  if (isFirstLogin) return <FirstLoginPasswordPage />;
  // Wait for the account before choosing its shell, as the route guard does.
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return (
      <div className="bm-root min-h-screen bg-white px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
        {loadError ? <div role="alert" className="pt-10"><ErrorState>{loadError}</ErrorState></div> : <Loading />}
      </div>
    );
  }
  return <Navigate to={passwordPathFor(permissions) || '/'} replace />;
};

const ChangePassword = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isFirstLogin = searchParams.get('firstLogin') === '1';
  const shell = shellFor(location.pathname);
  if (shell) return <ShellPasswordPage eyebrow={shell.eyebrow} isFirstLogin={isFirstLogin} />;
  return <StandalonePassword isFirstLogin={isFirstLogin} />;
};

export default ChangePassword;
